"use client";
import React, { useState, useEffect, useRef, use } from "react";
import { X, MessageSquare, ChevronLeft, ChevronRight } from "lucide-react";
import { Resizable } from "re-resizable";
import { v4 as uuidv4 } from "uuid";
import { useDatabase } from "./DatabaseSelector";
import TestDriveForm from "./TestDriveForm";
import UserInfoForm from "./UserInfoForm";
import AudioRecorder from "./Recorder";
import StreamingAudioPlayer from "./Audioplayer";
import useWebSocket from "react-use-websocket"
import { ReadyState } from "react-use-websocket"

interface Message {
  type:
    | "text"
    | "vehicle_models"
    | "vehicleVariants"
    | "variant_information"
    | "compare_variant"
    | "form"
    | "test_drive_form"
    | "user_info_form"
    | "product_recommendation";
  content: string | null;
  sender: "user" | "bot";
  recommendations?: string[];
  vehicle_models?: string[];
  vehicleVariants?: VehicleVariant[];
  variant_information?: VariantInformation[];
  compare_variant?: CompareVariant[];
  product_recommendation?: ProductRecommendation[];
  metadata?: string[];
  request_form?: boolean;
  user_info_form?: boolean;
  user_form_values?: FormPayload;
}

interface FormPayload {
  // Define the properties of the form data here
  title: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  civilId: string;
}

interface VariantInformation {
  // existing properties...
  [key: string]: any; // add this index signature
}

interface VehicleVariant {
  brand: string;
  variant: string;
  model: string;
  engine_type: string;
  transmission: string;
  price_information: number;
  drive: string;
  fuel: string;
  image_src: string;
}

interface ProductRecommendation {
  brand: string;
  model: string;
  variant: string;
  engine_type: string;
  transmission: string;
  price: string;
  drive: string;
  fuel: string;
  image_src: string;
}

interface VariantInformation {
  brand: string;
  model: string;
  variant: string;
  engine_specifications: {
    transmission: string;
    engine_displacement_cc: string;
    power_output_hp: string;
    engine_type: string;
  };
  price_information: {
    ex_showroom_price: number;
    EMI_options: {
      finance_estimate: number;
      estimated_emi_months: string;
    };
  };
  drive: string;
  fuel: string;
  colors: string[];
  image_src: string;
}

interface CompareVariant {
  variant: string;
  details: VariantInformation[];
}

const TypingIndicator = () => (
  <div className="flex items-center space-x-2 bg-gray-200 rounded-lg p-3 max-w-[80px]">
    <div className="typing-dot"></div>
    <div className="typing-dot animation-delay-200"></div>
    <div className="typing-dot animation-delay-400"></div>
  </div>
);

function greet<T extends string>(message: T): Capitalize<T> {
  // Note: This is just for type demonstration.
  // The actual capitalization needs to be done at runtime.
  return (message.charAt(0).toUpperCase() + message.slice(1)) as Capitalize<T>;
}

interface LLMmodelProps {
  apiKey: string;
  selectedOption: string;
}

const CustomerSupportChatbot: React.FC<LLMmodelProps> = ({apiKey,selectedOption}) => 
  {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      type: "text",
      content: "Welcome! How can I assist you today?",
      sender: "bot",
    },
  ]);
  const { selectedDatabase } = useDatabase();
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [currentCarIndex, setCurrentCarIndex] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [inputEnabled, setInputEnabled] = useState(true);
  // const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [submittedForms, setSubmittedForms] = useState<{[key: string]: boolean}>({
    userInfo: false,
    testDrive: false
  });
  const [windowSize, setWindowSize] = useState({ width: 400, height: 600 });
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [userInfoData, setUserInfoData] = useState<FormPayload | null>(null);
  const [testDriveData, setTestDriveData] = useState<any | null>(null);
  const [currentRecommendationIndex, setCurrentRecommendationIndex] = useState(0);
  const sessionId = useRef(uuidv4());

  const [socketUrl] = useState("wss://interim-cab-module-api.ispgnet.com/chat/")
  //const [socketUrl] = useState("ws://localhost:8000/chat/")
  const { sendMessage, lastMessage, readyState } = useWebSocket(socketUrl)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (lastMessage !== null) {
      const data = JSON.parse(lastMessage.data)
      handleWebSocketMessage(data)
    }
    console.log(lastMessage)
  }, [lastMessage])

  const handleWebSocketMessage = (data: any) => {
    setIsTyping(false);

      if (data.request_form) {
        setMessages((prev) => [
          ...prev,
          {
            type: "test_drive_form",
            content: null,
            sender: "bot",
            request_form: true,
          },
        ]);
        setInputEnabled(false);
      } else if (data.user_info_form) {
        setMessages((prev) => [
          ...prev,
          {
            type: "user_info_form",
            content: null,
            sender: "bot",
            user_info_form: true,
            user_form_values: data.user_form_values
          },
        ]);
        console.log(data.user_form_values);
        setInputEnabled(false);
      } else if (data.vehicle_models && data.vehicle_models.length > 0) {
        setMessages((prev) => [
          ...prev,
          {
            type: "vehicle_models",
            content: null,
            sender: "bot",
            vehicle_models: data.vehicle_models,
          },
        ]);
      } else if (data.vehiclevariants && data.vehiclevariants.length > 0) {
        setMessages((prev) => [
          ...prev,
          {
            type: "vehicleVariants",
            content: null,
            sender: "bot",
            vehicleVariants: data.vehiclevariants,
            product_recommendation: data.product_recommendation,
          },
        ]);
      } else if (
        data.variant_information &&
        data.variant_information.length > 0
      ) {
        setMessages((prev) => [
          ...prev,
          {
            type: "variant_information",
            content: null,
            sender: "bot",
            variant_information: data.variant_information,
          },
        ]);
      } else if (data.compare_variant && data.compare_variant.length > 0) {
        setMessages((prev) => [
          ...prev,
          {
            type: "compare_variant",
            content: null,
            sender: "bot",
            compare_variant: data.compare_variant,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            type: "text",
            content:
              data.responses ||
              "Make sure to select a Project from the dropdown. Please try again.",
            sender: "bot",
            recommendations: data.recommendations || [],
            metadata: data.metadata || [],
          },
        ]);
      }
  };

  const handleSendMessage = async (message: string = inputMessage, testDrivePayload?: any, userInfoPayload?: FormPayload) => {
    if (!message.trim()) return
    setLoading(true)
    setMessages((prev) => [...prev, { type: "text", content: message, sender: "user" }])
    setInputMessage("")
    setIsTyping(true)

    try {
      sendMessage(
        JSON.stringify({
          question: message,
          session_id: sessionId.current,
          Database_name: selectedDatabase,
          llm_model: selectedOption,
          test_drive_data: testDrivePayload ?? {},
          user_info_data: userInfoPayload ?? {}, 
        })
      );
    } catch (error) {
      console.error("Error sending message:", error)
      setIsTyping(false)
      setMessages((prev) => [...prev,
        {
          type: "text",
          content: "Sorry, I encountered an error. Please try again later.",
          sender: "bot",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleTestDriveSubmit = async (testDriveFormData: any) => {
    try {
      // Store the form data in state
      setTestDriveData(testDriveFormData);

      const response = await handleSendMessage(
        "test_drive_form_submitted",
        testDriveFormData
      );

      setMessages((prev) => [
        ...prev,
        {
          type: "text",
          content:"Thank you for scheduling your test drive! Our team will confirm the details shortly.",
          sender: "bot",
        },
      ]);

      setInputEnabled(true);
      setSubmittedForms(prev => ({...prev, testDrive: true}));
    } catch (error) {
      console.error("Error submitting test drive form:", error);
      setMessages((prev) => [
        ...prev,
        {
          type: "text",
          content:
            "Sorry, there was an error scheduling your test drive. Please try again later.",
          sender: "bot",
        },
      ]);
    }
  };

  const handleUserInfoSubmit = async (userData: FormPayload) => {
    try {
      // Store the form data in state
      setUserInfoData(userData);

      // Call the API with the user info data
      const response = await handleSendMessage(
        "user_info_form_submitted",
        undefined,
        userData
      );

      setMessages((prev) => [
        ...prev,
        {
          type: "text",
          content: "Thank you for submitting your information!",
          sender: "bot",
        },
      ]);

      setInputEnabled(true);
      setSubmittedForms(prev => ({...prev, userInfo: true}));
    } catch (error) {
      console.error("Error submitting user info form:", error);
      setMessages((prev) => [
        ...prev,
        {
          type: "text",
          content:
            "Sorry, there was an error submitting your information. Please try again later.",
          sender: "bot",
        },
      ]);
    }
  };

  const handleFormCancel = () => {
    setInputEnabled(true);
    setMessages((prev) => [
      ...prev,
      {
        type: "text",
        content:
          "No problem! Let me know if you'd like to schedule a test drive later.",
        sender: "bot",
      },
    ]);
  };

  const handleShowInterest = () => {
    setMessages((prev) => {
      const hasForm = prev.some((message) => message.type === "form");
      if (hasForm) {
        return prev;
      }

      return [...prev, { type: "form", content: null, sender: "user" }];
    });
  };

  const handleSubmitInterest = async (e: React.FormEvent) => {
    e.preventDefault();
    const currentMessage = messages[messages.length - 1];
    const currentVariant = currentMessage?.vehicleVariants?.[currentCarIndex];

    // Ensure we have a valid VehicleVariant, not a ProductRecommendation
    if (!currentVariant || !("model" in currentVariant)) {
      console.error("Invalid vehicle data");
      return;
    }

    try {
      const response = await fetch("https://scrape-graph-api-dev.ispgnet.com/ingest/saveInterest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          car: `${currentVariant.brand} ${currentVariant.model} ${currentVariant.variant}`,
        }),
      }
    );

      if (!response.ok) {
        throw new Error("Failed to save interest");
      }

      setMessages((prev) => [
        ...prev,
        {
          type: "text",
          content: `Thank you ${name}! We'll contact you at ${email} about the ${currentVariant.brand} ${currentVariant.model} ${currentVariant.variant}.`,
          sender: "bot",
        },
      ]);
      setName("");
      setEmail("");
      setInputEnabled(true);
      // setIsFormSubmitted(true);
    } catch (error) {
      console.error("Error saving interest:", error);
      setMessages((prev) => [
        ...prev,
        {
          type: "text",
          content:
            "Sorry, there was an error saving your interest. Please try again later.",
          sender: "bot",
        },
      ]);
    }
  };

  const renderVehicleModelButtons = (models: string[]) => {
    return (
      <div className="grid grid-cols-2 gap-2 w-full">
        {models.map((model, index) => (
          <button
            key={index}
            onClick={() => handleSendMessage(`Show me variants of ${model}`)}
            className="bg-gray-200 text-black py-2 px-4 rounded text-sm 
                      border border-black 
                      hover:bg-gray-300 hover:border-gray-700 
                      transition-all duration-200 ease-in-out"
          >
            {model}
          </button>
        ))}
      </div>
    );
  };

  const renderVehicleCard = (
    variant: VehicleVariant | ProductRecommendation
  ) => {
    return (
      <div className="w-full bg-white text-black shadow-lg rounded-xl">
        <div className="p-4">
          <img
            src={
              variant.image_src || (variant as ProductRecommendation).image_src
            }
            alt={`${variant.brand} ${variant.model} ${variant.variant}`}
            className="w-full h-40 object-cover mb-2 bg-gray-200 rounded-lg"
          />
          <div className="flex flex-col items-center text-center">
            <h2 className="font-semibold text-base">
              {variant.brand} {variant.model}
            </h2>
            <h3 className="font-medium text-base">
              Variant: {variant.variant}
            </h3>
            <p>Engine: {variant.engine_type}</p>
            <p>Transmission: {variant.transmission}</p>
            <p>
              Drive: {variant.drive} Fuel: {variant.fuel}
            </p>
            <p>
              Price: ₹
              {(
                variant as VehicleVariant
              ).price_information?.toLocaleString() ||
                (variant as ProductRecommendation).price}
            </p>
          </div>
          <button
            onClick={handleShowInterest}
            className="mt-2 w-full bg-primary text-white py-2 px-4 rounded hover:bg-gray-800 transition-colors"
          >
            Show Interest
          </button>
        </div>
      </div>
    );
  };

  const renderVehicleCards = (
    variants: VehicleVariant[],
    recommendations: ProductRecommendation[] | undefined
  ) => {
    return (
      <div className="relative w-[80%]">
        {/* Vehicle Variants Carousel */}
        <div className="relative flex overflow-x-hidden">
          {variants.map((variant, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-full transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentCarIndex * 100}%)` }}
            >
              {renderVehicleCard(variant)}
            </div>
          ))}
          {variants.length > 1 && (
            <>
              <button
                onClick={() =>
                  setCurrentCarIndex((prev) =>
                    prev > 0 ? prev - 1 : variants.length - 1
                  )
                }
                className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-primary text-white rounded-full p-2"
                aria-label="Previous car"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={() =>
                  setCurrentCarIndex((prev) =>
                    prev < variants.length - 1 ? prev + 1 : 0
                  )
                }
                className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-primary text-white rounded-full p-2"
                aria-label="Next car"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
        </div>

        {/* Recommendations Section */}
        {recommendations && recommendations.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowRecommendations(!showRecommendations)}
              className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-primary transition-colors"
            >
              {showRecommendations
                ? "Hide Recommendations"
                : "View Recommendations"}
            </button>

            {showRecommendations && (
              <div className="relative mt-4">
                {/* Recommendations Carousel */}
                <div className="flex overflow-x-hidden">
                  {recommendations.map((recommendation, index) => (
                    <div
                      key={index}
                      className="flex-shrink-0 w-full transition-transform duration-300 ease-in-out"
                      style={{
                        transform: `translateX(-${
                          currentRecommendationIndex * 100
                        }%)`,
                      }}
                    >
                      {renderVehicleCard(recommendation)}
                    </div>
                  ))}
                  {recommendations.length > 1 && (
                    <>
                      <button
                        onClick={() =>
                          setCurrentRecommendationIndex((prev) =>
                            prev > 0 ? prev - 1 : recommendations.length - 1
                          )
                        }
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-primary text-white rounded-full p-2"
                        aria-label="Previous recommendation"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        onClick={() =>
                          setCurrentRecommendationIndex((prev) =>
                            prev < recommendations.length - 1 ? prev + 1 : 0
                          )
                        }
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-primary text-white rounded-full p-2"
                        aria-label="Next recommendation"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderVariantInformationTable = (variantInfo: VariantInformation) => {
    return (
      <div className="overflow-x-auto w-full">
        <table className="min-w-full bg-white border border-gray-300 rounded-lg">
          <tbody>
            {Object.entries(variantInfo).map(([key, value]) => {
              if (
                typeof value === "object" &&
                value !== null &&
                key !== "image_src"
              ) {
                return (
                  <React.Fragment key={key}>
                    <tr className="bg-gray-50">
                      <td className="py-2 px-4 border-b font-bold " colSpan={2}>
                        {key.replace(/_/g, " ").toUpperCase()}
                      </td>
                    </tr>
                    {Object.entries(value).map(([subKey, subValue]) => (
                      <tr key={`${key}-${subKey}`}>
                        <td className="py-2 px-4 border-b font-bold">
                          {greet(subKey.replace(/_/g, " "))}
                        </td>
                        <td className="py-2 px-4 border-b">
                          {typeof subValue === "object" && subValue !== null
                            ? Object.entries(subValue).map(
                                ([subSubKey, subSubValue]) => (
                                  <div key={subSubKey}>{`${subSubKey.replace(
                                    /_/g,
                                    " "
                                  )}: ${subSubValue}`}</div>
                                )
                              )
                            : String(subValue)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              } else if (Array.isArray(value)) {
                return (
                  <tr key={key}>
                    <td className="py-2 px-4 border-b">
                      {key.replace(/_/g, " ")}
                    </td>
                    <td className="py-2 px-4 border-b">{value.join(", ")}</td>
                  </tr>
                );
              } else if (key !== "image_src") {
                return (
                  <tr key={key}>
                    <td className="py-2 px-4 border-b">
                      {key.replace(/_/g, " ")}
                    </td>
                    <td className="py-2 px-4 border-b">{value}</td>
                  </tr>
                );
              }
              return null;
            })}
          </tbody>
        </table>
        <div className="mt-4">
          <img
            src={variantInfo.image_src}
            alt={`${variantInfo.brand} ${variantInfo.model} ${variantInfo.variant}`}
            className="w-full h-auto rounded-lg"
          />
        </div>
      </div>
    );
  };

  const renderCompareVariantTable = (variants: CompareVariant[]) => {
    if (!variants || variants.length === 0) return null;

    const variantInfo1 = variants[0].details[0];
    const variantInfo2 = variants[1].details[0];

    return (
      <div className="overflow-x-auto w-full">
        <table className="min-w-full bg-white border border-gray-300 rounded-lg">
          <tbody>
            {Object.keys(variantInfo1)
              .filter((key) => key !== "image_src")
              .map((key, index) => {
                if (
                  typeof variantInfo1[key] === "object" &&
                  variantInfo1[key] !== null
                ) {
                  return (
                    <React.Fragment key={key}>
                      <tr className="bg-gray-50">
                        <td
                          className="py-2 px-4 border-b font-bold"
                          colSpan={3}
                        >
                          {key.replace(/_/g, " ").toUpperCase()}
                        </td>
                      </tr>
                      {Object.keys(variantInfo1[key]).map(
                        (subKey, subIndex) => (
                          <tr key={`${key}-${subKey}`}>
                            <td className="py-2 px-4 border-b font-bold">
                              {greet(subKey.replace(/_/g, " "))}
                            </td>
                            <td className="py-2 px-4 border-b">
                              {renderCompareVariantValue(
                                variantInfo1,
                                key,
                                subKey
                              )}
                            </td>
                            <td className="py-2 px-4 border-b">
                              {renderCompareVariantValue(
                                variantInfo2,
                                key,
                                subKey
                              )}
                            </td>
                          </tr>
                        )
                      )}
                    </React.Fragment>
                  );
                } else if (Array.isArray(variantInfo1[key])) {
                  return (
                    <tr key={key}>
                      <td className="py-2 px-4 border-b">
                        {key.replace(/_/g, " ")}
                      </td>
                      <td className="py-2 px-4 border-b">
                        {variantInfo1[key].join(", ")}
                      </td>
                      <td className="py-2 px-4 border-b">
                        {variantInfo2[key].join(", ")}
                      </td>
                    </tr>
                  );
                } else if (key !== "image_src") {
                  return (
                    <tr key={key}>
                      <td className="py-2 px-4 border-b">
                        {key.replace(/_/g, " ")}
                      </td>
                      <td className="py-2 px-4 border-b">
                        {variantInfo1[key]}
                      </td>
                      <td className="py-2 px-4 border-b">
                        {variantInfo2[key]}
                      </td>
                    </tr>
                  );
                }
                return null;
              })}
          </tbody>
        </table>
        <div className="mt-4 flex justify-around">
          {variants.map((variant, index) => (
            <div key={index} className="w-1/3 px-2">
              <img
                src={variant.details[0].image_src}
                alt={`${variant.details[0].brand} ${variant.details[0].model} ${variant.variant}`}
                className="w-full h-auto rounded-lg"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCompareVariantValue = (
    detail: any,
    key: string,
    subKey?: string
  ) => {
    if (subKey) {
      if (
        typeof detail[key][subKey] === "object" &&
        detail[key][subKey] !== null
      ) {
        return Object.keys(detail[key][subKey]).map(
          (subSubKey, subSubIndex) => (
            <div key={subSubKey}>{`${subSubKey.replace(/_/g, " ")}: ${
              detail[key][subKey][subSubKey]
            }`}</div>
          )
        );
      } else {
        return detail[key][subKey];
      }
    } else {
      if (typeof detail[key] === "object" && detail[key] !== null) {
        return Object.keys(detail[key]).map((subKey, subIndex) => (
          <div key={subKey}>{`${subKey.replace(/_/g, " ")}: ${
            detail[key][subKey]
          }`}</div>
        ));
      } else if (Array.isArray(detail[key])) {
        return detail[key].join(", ");
      } else {
        return detail[key];
      }
    }
  };
  const connectionStatus = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  }[readyState]
  
  return (
    <div className="fixed bottom-4 right-4">
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-5 flex items-center rounded-full bg-primary text-white py-2 px-4 transition-transform transition-colors duration-300 ease-in-out hover:scale-105"
        >
          <MessageSquare className="mr-2 h-4 w-4" />
          Chat
        </button>
      )}
      {isOpen && (
        <Resizable
          size={{ width: windowSize.width, height: windowSize.height }}
          minWidth={300}
          minHeight={400}
          maxWidth={500}
          maxHeight={650}
          onResizeStop={(e, direction, ref, d) => {
            setWindowSize({
              width: parseFloat(ref.style.width),
              height: parseFloat(ref.style.height),
            });
          }}
          className="fixed w-80 h-[500px] flex flex-col bg-white text-black shadow-lg rounded-lg"
        >
          <div className="flex justify-between items-center mb-4 p-4 border-b handle bg-primary rounded-t-xl">
            <h2 className="text-lg font-semibold text-white">
              Customer Support
            </h2>
            <button
              onClick={() => setIsOpen(false)}
              className="rounded-full p-2 hover:bg-gray-200 hover:text-white bg-primary transition-colors"
              aria-label="Close chat"
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </div>
          <div className="flex-grow overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${
                  msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
              >
                {msg.type === "text" && msg.content && (
                  <div
                    className={`flex flex-col rounded-lg p-2 max-w-[80%] text-justify ${
                      msg.sender === "user"
                        ? "bg-primary text-white"
                        : "bg-gray-200"
                    }`}
                  >
                    <div>{msg.content}</div>
                    {msg.sender === "bot" && (
                      <StreamingAudioPlayer
                        text={msg.content}
                        disabled={!msg.content}
                        isLatestMessage={index === messages.length - 1}
                      />
                    )}
                    {msg.metadata && msg.metadata.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="text-blue-500 text-sm">Sources:</span>
                        {msg.metadata.map((item, i) => (
                          <a
                            key={i}
                            href={item}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline text-sm"
                          >
                            [{i + 1}]
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Test drive form */}
                {msg.type === "test_drive_form" &&
                msg.request_form &&
                !submittedForms.testDrive && (
                  <div className="w-full">
                    <TestDriveForm
                      onSubmit={handleTestDriveSubmit}
                      onCancel={handleFormCancel}
                    />
                  </div>
                )}
                {/* User info form */}
                {msg.type === "user_info_form" &&
                msg.user_info_form &&
                !submittedForms.userInfo && (
                  <div className="w-full">
                    <UserInfoForm
                      onSubmit={(userData) => handleUserInfoSubmit(userData as FormPayload)}
                      initialValues={msg.user_form_values}
                    />
                  </div>
                )}
                {msg.type === "vehicle_models" &&
                  msg.vehicle_models &&
                  renderVehicleModelButtons(msg.vehicle_models)}
                {msg.type === "vehicleVariants" &&
                  msg.vehicleVariants &&
                  renderVehicleCards(
                    msg.vehicleVariants,
                    msg.product_recommendation
                  )}
                {msg.type === "variant_information" &&
                  msg.variant_information &&
                  renderVariantInformationTable(msg.variant_information[0])}
                {msg.type === "compare_variant" &&
                  msg.compare_variant &&
                  renderCompareVariantTable(msg.compare_variant)}
                {msg.type === "form" && !isFormSubmitted && (
                  <form
                    onSubmit={handleSubmitInterest}
                    className="space-y-2 w-[80%]"
                  >
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full rounded-lg border border-gray-300 bg-white text-black p-2"
                    />
                    <input
                      type="email"
                      placeholder="Your Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full rounded-lg border border-gray-300 bg-white text-black p-2"
                    />
                    <button
                      type="submit"
                      className="w-full bg-primary text-white py-2 px-4 rounded"
                    >
                      Submit Interest
                    </button>
                  </form>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <TypingIndicator />
              </div>
            )}
            {messages[messages.length - 1]?.sender === "bot" &&
              messages[messages.length - 1]?.recommendations && (
                <div className="flex flex-wrap justify-end gap-2 mt-2 w-[80%] ml-auto">
                  {messages[messages.length - 1].recommendations?.map(
                    (recommendation, index) => (
                      <button
                        key={index}
                        onClick={() => handleSendMessage(recommendation)}
                        className="bg-gray-200 text-black py-1 px-2 rounded text-sm 
                                    border border-black 
                                    hover:bg-gray-300 hover:border-gray-700 
                                    transition-all duration-200 ease-in-out"
                      >
                        {recommendation}
                      </button>
                    )
                  )}
                </div>
              )}
            <div ref={messagesEndRef} />
          </div>
          {inputEnabled && (
            <div className="p-4 border-t flex space-x-2">
              <input
                type="text"
                placeholder="Type a message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSendMessage();
                  }
                }}
                className="flex-grow rounded-lg border border-gray-300 p-2"
                disabled={loading}
              />
              <AudioRecorder
                onTranscriptionComplete={(text: string) =>
                  setInputMessage(text)
                }
              />
              <button
                onClick={() => handleSendMessage()}
                className="bg-primary text-white py-2 px-4 rounded flex items-center"
                disabled={loading}
              >
                Send
              </button>
            </div>
          )}
        </Resizable>
      )}
      <style>{`
        .loader {
          border: 2px solid #f3f3f3;
          border-top: 2px solid #333;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .typing-dot {
          width: 8px;
          height: 8px;
          background-color: #666;
          border-radius: 50%;
          animation: typingAnimation 1.4s infinite ease-in-out;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        @keyframes typingAnimation {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
      `}</style>
    </div>
  );
};

export default CustomerSupportChatbot;
